import { ScreenLink as Link } from './permissions/ScreenLink';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions';
import { screenForPath } from './permissions/screenAccess';

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface HierarchyBreadcrumbProps {
  items: BreadcrumbItem[];
  className?: string;
}

/**
 * Quiet text breadcrumb above a detail page title (DESIGN_SYSTEM.md §6):
 * no box, muted links, the current page in primary text. Follows the
 * document direction; the separator chevron mirrors in LTR.
 */
export default function HierarchyBreadcrumb({ items: all, className = '' }: HierarchyBreadcrumbProps) {
  const { t } = useTranslation();
  const { canPath } = usePermissions();
  // A crumb that is a closed screen ("פרויקטים") is left out with its separator.
  const items = all.filter((i) => !i.href || canPath(i.href) || !screenForPath(i.href)?.exact);
  return (
    <nav aria-label={t('aria.breadcrumb')} className={className}>
      <ol className="flex items-center gap-1 text-sm flex-wrap min-w-0">
        {items.map((item, idx) => {
          const last = idx === items.length - 1;
          return (
            <li key={idx} className="flex items-center gap-1 min-w-0">
              {idx > 0 && <ChevronLeft size={14} className="text-surface-400 flex-shrink-0 ltr:rotate-180" aria-hidden="true" />}
              {item.href ? (
                <Link
                  to={item.href}
                  className="text-surface-600 hover:text-primary-700 hover:underline underline-offset-2 transition-colors duration-150 truncate max-w-56 rounded-xs"
                >
                  {item.label}
                </Link>
              ) : (
                <span className="text-surface-900 font-medium truncate max-w-56" aria-current={last ? 'page' : undefined}>
                  {item.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
